/**
 * features/hr/PayrollBankExport.tsx
 * ------------------------------------------------------------------
 * HR PAYROLL — Bank Export + overall summary (the "marksheet").
 *
 * From a month's GENERATED payslips this produces:
 *   1. Bank payment file (CSV) — account name / number / bank /
 *      branch code / NET amount / reference, ready to upload to the
 *      bank for salary disbursement.
 *   2. Overall summary — a per-employee table of gross / PAYE /
 *      deductions / net with grand totals, plus a Summary CSV.
 *
 * Source = payslips, so generate the Pay Run for the month first.
 * ------------------------------------------------------------------ */
import { useMemo, useState } from 'react';
import { Landmark, Download, AlertTriangle } from 'lucide-react';
import { SoftCard, StatChip, glassFieldClass } from '@/components/ui/glass';
import { Spinner } from '@/components/ui/primitives';
import { useHrData } from '@/features/hr/useHrData';
import { periodLabel } from '@/lib/assemblePayslip';
import { fmtPula, round2, MONTH_LABELS } from '@/lib/hrPayroll';

function csvCell(v: string): string {
  return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
}
function download(csv: string, name: string) {
  const blob = new Blob([csv], { type: 'text/csv' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = name;
  a.click();
  URL.revokeObjectURL(a.href);
}

export default function PayrollBankExport() {
  const data = useHrData();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);

  const period = { year, month, from: '', to: '' };
  const tag = `${year}-${String(month).padStart(2, '0')}`;

  const rows = useMemo(() => {
    return data.payslips
      .filter((p) => p.period?.year === year && p.period?.month === month)
      .map((p) => ({
        name: p.staffName,
        employeeNo: p.employeeNo,
        gross: p.earningsTotal,
        paye: p.paye,
        deductions: p.deductionsTotal,
        net: p.net,
        bankName: p.bank?.bankName ?? '',
        accountName: p.bank?.accountName ?? '',
        accountNumber: p.bank?.accountNumber ?? '',
        branchCode: p.bank?.branchCode ?? '',
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [data.payslips, year, month]);

  const totals = useMemo(() => {
    const s = (sel: (r: typeof rows[number]) => number) => round2(rows.reduce((a, r) => a + sel(r), 0));
    return { gross: s((r) => r.gross), paye: s((r) => r.paye), deductions: s((r) => r.deductions), net: s((r) => r.net) };
  }, [rows]);

  const missingBank = rows.filter((r) => !r.accountNumber).length;

  function exportBankFile() {
    const header = ['Account Name', 'Account Number', 'Branch Code', 'Amount (BWP)', 'Reference'];
    const body = rows.map((r) => [
      r.accountName || r.name, r.accountNumber, r.branchCode, r.net.toFixed(2), `${MONTH_LABELS[month - 1]} ${year} salary`,
    ]);
    const footer = ['', '', 'TOTAL', totals.net.toFixed(2), `${rows.length} payments`];
    const csv = [header, ...body, footer].map((row) => row.map(csvCell).join(',')).join('\n');
    download(csv, `bank-payment_${tag}.csv`);
  }

  function exportSummary() {
    const header = ['Employee No', 'Name', 'Gross', 'PAYE', 'Deductions', 'Net'];
    const body = rows.map((r) => [r.employeeNo, r.name, r.gross.toFixed(2), r.paye.toFixed(2), r.deductions.toFixed(2), r.net.toFixed(2)]);
    const footer = ['', `TOTAL (${rows.length})`, totals.gross.toFixed(2), totals.paye.toFixed(2), totals.deductions.toFixed(2), totals.net.toFixed(2)];
    const csv = [header, ...body, footer].map((row) => row.map(csvCell).join(',')).join('\n');
    download(csv, `payroll-summary_${tag}.csv`);
  }

  if (data.loading) {
    return <div className="flex justify-center py-16"><Spinner label="Loading payroll…" /></div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-gray-500 dark:text-gray-400">{periodLabel(period)} · from generated payslips</p>
        <div className="flex items-center gap-2">
          <select value={month} onChange={(e) => setMonth(Number(e.target.value))} className={glassFieldClass('py-1.5 text-sm')}>
            {MONTH_LABELS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
          <input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} className={glassFieldClass('py-1.5 text-sm w-24')} />
        </div>
      </div>

      {rows.length === 0 ? (
        <SoftCard hover={false}>
          <div className="flex items-center gap-2 text-amber-700 dark:text-amber-300 text-sm">
            <AlertTriangle size={16} />
            No payslips generated for {periodLabel(period)} yet. Run the Pay Run for this month first.
          </div>
        </SoftCard>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatChip tone="blue" value={fmtPula(totals.gross)} label="Total gross" />
            <StatChip tone="gold" value={fmtPula(totals.paye)} label="Total PAYE" />
            <StatChip tone="red" value={fmtPula(totals.deductions)} label="Total deductions" />
            <StatChip tone="emerald" value={fmtPula(totals.net)} label="Net payable (to bank)" />
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={exportBankFile} className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-[var(--accent-strong,#1b3a6b)] hover:bg-[var(--accent-strong-hover,#162e52)] rounded-xl">
              <Landmark size={15} /> Bank payment CSV
            </button>
            <button onClick={exportSummary} className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600">
              <Download size={15} /> Summary CSV
            </button>
            {missingBank > 0 && (
              <span className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-300">
                <AlertTriangle size={14} /> {missingBank} without bank account on file
              </span>
            )}
          </div>

          <SoftCard pad="p-0" hover={false}>
            <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700">
              <h3 className="font-semibold text-gray-800 dark:text-gray-100 text-sm">Payroll summary · {periodLabel(period)}</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                    <th className="py-2 px-4 font-medium">Employee</th>
                    <th className="py-2 px-4 font-medium">Bank</th>
                    <th className="py-2 px-4 font-medium text-right">Gross</th>
                    <th className="py-2 px-4 font-medium text-right">PAYE</th>
                    <th className="py-2 px-4 font-medium text-right">Deductions</th>
                    <th className="py-2 px-4 font-medium text-right">Net</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
                  {rows.map((r) => (
                    <tr key={r.employeeNo}>
                      <td className="py-2 px-4 text-gray-800 dark:text-gray-100">{r.name} <span className="text-xs text-gray-400">#{r.employeeNo}</span></td>
                      <td className="py-2 px-4 text-gray-500 dark:text-gray-400 text-xs">
                        {r.accountNumber ? `${r.bankName || '—'} · ${r.accountNumber}` : <span className="text-amber-500">no account</span>}
                      </td>
                      <td className="py-2 px-4 text-right text-gray-700 dark:text-gray-200">{fmtPula(r.gross)}</td>
                      <td className="py-2 px-4 text-right text-gray-600 dark:text-gray-300">{fmtPula(r.paye)}</td>
                      <td className="py-2 px-4 text-right text-gray-600 dark:text-gray-300">{fmtPula(r.deductions)}</td>
                      <td className="py-2 px-4 text-right font-semibold text-gray-800 dark:text-gray-100">{fmtPula(r.net)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-gray-200 dark:border-gray-600 font-semibold text-gray-800 dark:text-gray-100">
                    <td className="py-2.5 px-4" colSpan={2}>Total · {rows.length} staff</td>
                    <td className="py-2.5 px-4 text-right">{fmtPula(totals.gross)}</td>
                    <td className="py-2.5 px-4 text-right">{fmtPula(totals.paye)}</td>
                    <td className="py-2.5 px-4 text-right">{fmtPula(totals.deductions)}</td>
                    <td className="py-2.5 px-4 text-right">{fmtPula(totals.net)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </SoftCard>
        </>
      )}
    </div>
  );
}
